import { useEffect, useState, useCallback } from "react";
import { Map } from "lucide-react";
import api from "../lib/api";
import { PRIORITIES } from "../lib/helpers";
import TaskModal from "../components/TaskModal";

const DAY_MS = 86400000;
const toDate = (s) => new Date(s.slice(0, 10) + "T00:00:00");

export default function RoadmapPage() {
  const [groups, setGroups] = useState(null);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, u] = await Promise.all([api.get("/roadmap"), api.get("/users")]);
      setGroups(r.data);
      setUsers(u.data);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!groups) return <div className="p-8 text-zinc-500">Cargando roadmap…</div>;

  const withTasks = groups.filter((g) => g.tasks.length > 0);
  const allTasks = withTasks.flatMap((g) => g.tasks);

  if (allTasks.length === 0) {
    return (
      <div className="h-screen overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2" data-testid="roadmap-title"><Map className="h-7 w-7 text-primary" /> Roadmap</h1>
          <p className="text-zinc-500 mt-6 border border-dashed border-white/10 rounded-2xl p-10 text-center text-sm">
            No hay tareas con fechas todavía. Añade fecha de inicio o vencimiento a las tareas para verlas en el roadmap global.
          </p>
        </div>
      </div>
    );
  }

  const starts = allTasks.map((t) => toDate(t.start_date || t.due_date).getTime());
  const ends = allTasks.map((t) => toDate(t.due_date || t.start_date).getTime());
  const min = Math.min(...starts) - 2 * DAY_MS;
  const max = Math.max(...ends) + 4 * DAY_MS;
  const total = max - min;
  const pct = (ms) => ((ms - min) / total) * 100;
  const todayPct = pct(Date.now());
  const step = total > 150 * DAY_MS ? 30 * DAY_MS : 7 * DAY_MS;
  const ticks = [];
  for (let t = min; t <= max; t += step) ticks.push(t);

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2 mb-1" data-testid="roadmap-title"><Map className="h-7 w-7 text-primary" /> Roadmap</h1>
        <p className="text-sm text-zinc-500 mb-6">Cronograma global de todos tus proyectos.</p>

        <div className="border border-white/5 rounded-xl bg-[#0E0E10] overflow-hidden">
          <div className="relative h-8 border-b border-white/5">
            {ticks.map((t) => (
              <span key={t} className="absolute top-1/2 -translate-y-1/2 text-[10px] text-zinc-600 pl-1 border-l border-white/5 h-full flex items-center" style={{ left: `${pct(t)}%` }}>
                {new Date(t).toLocaleDateString("es", { day: "numeric", month: "short" })}
              </span>
            ))}
          </div>
          <div className="relative">
            {todayPct >= 0 && todayPct <= 100 && <div className="absolute inset-y-0 w-px bg-primary z-10" style={{ left: `${todayPct}%` }} title="Hoy" />}
            {ticks.map((t) => <div key={t} className="absolute inset-y-0 w-px bg-white/[0.03]" style={{ left: `${pct(t)}%` }} />)}
            {withTasks.map((g, gi) => {
              const colColor = (status) => g.board.columns.find((c) => c.id === status)?.color || "#71717A";
              return (
                <div key={g.board.id} className="border-b border-white/5 last:border-b-0 animate-fade-up" style={{ animationDelay: `${Math.min(gi * 80, 480)}ms` }} data-testid={`roadmap-board-${g.board.id}`}>
                  <div className="px-3 pt-3 pb-1 flex items-center gap-2">
                    <h3 className="font-display font-bold text-sm">{g.board.name}</h3>
                    {g.board.client && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#00B0FF]/15 text-[#00B0FF]">{g.board.client}</span>}
                    <span className="text-xs text-zinc-600">{g.tasks.length} tarea{g.tasks.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="pb-2">
                    {g.tasks.map((t) => {
                      const s = toDate(t.start_date || t.due_date).getTime();
                      const e = toDate(t.due_date || t.start_date).getTime() + DAY_MS;
                      const left = pct(s), width = Math.max(1.2, pct(e) - left);
                      const pr = PRIORITIES[t.priority] || PRIORITIES.medium;
                      return (
                        <div key={t.id} className="relative h-8">
                          <button onClick={() => setActive({ task: t, board: g.board })} data-testid={`roadmap-bar-${t.id}`}
                            className="absolute top-1 h-6 rounded-md flex items-center gap-1.5 px-2 text-[11px] font-medium truncate hover:brightness-125 transition-all"
                            style={{ left: `${left}%`, width: `${width}%`, background: `${colColor(t.status)}33`, border: `1px solid ${colColor(t.status)}66` }}>
                            <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: pr.color }} />
                            <span className="truncate">{t.title}</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {active && (
        <TaskModal task={active.task} board={active.board} users={users}
          onClose={() => setActive(null)}
          onUpdated={() => load()}
          onDeleted={() => { setActive(null); load(); }} />
      )}
    </div>
  );
}
